/* eslint-disable */
import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";
import { TreeView, TreeItem, Skeleton } from "@material-ui/lab";
import type { FC } from "react";
import { useStylesTreeView, useStylesTreeitem } from "./styles";
import { useHierarchicalSelector } from "./useHierarchicalSelector";
import Input from "./Input";
import Search from "./Search";
import Popover from "./Popover";
import AddBoxOutlinedIcon from "../../../../icons/OpenTree";
import { cloneDeep, orderBy, sortedUniq } from "lodash";
import RemoveOutlinedIcon from "../../../../icons/CloseTree";
import { makeStyles, createStyles, Theme, Menu, MenuItem } from "@material-ui/core";
import { hierarchyAllByChildId, hierarchies, Hierarchy, hierarchiesGetAllIds, hierarchiesFlat } from "../../../bulkexport";
import { equal, uniq } from "../../../../shared";
import * as UI from "@material-ui/core";
import CheckedIcon from "../../../../icons/Checked";
import UncheckedIcon from "../../../../icons/Unchecked";
import messages from "./messages";
import { sortCalendarItems } from "./sortItems";

const useTreeStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      overflow: "visible",
    },
    menu: {},
    menuItem: {
      fontSize: "12px",
      textTransform: "capitalize",
    },
    container: {
      height: "inherit",
      position: "relative",
    },
    scrollBox: {
      padding: theme.spacing(0.5, 1),
      overflow: "auto",
      height: "100%",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
    },
    scrollBoxMultiple: {
      boxShadow: "inset 0px -1px 4px #eaeaea",
      padding: theme.spacing(0.5, 1),
      overflow: "auto",
      height: "calc(100% - 48px)",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
    },
    selectButton: {
      margin: theme.spacing(1.5, 1),
      minWidth: theme.spacing(4),
      "&.Mui-disabled svg > path": {
        fill: theme.palette.action.disabled,
      },
    },
    selectButtonLabel: {
      fontSize: 14,
    },
    selectButtonIcon: {
      width: 15,
      height: 15,
    },
  })
);
const useParentStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& $content:hover": {
        background: "rgba(51, 51, 51, 0.05)",
      },
    },
    selected: {
      "& > $content": {
        background: "rgba(101, 172, 30, 0.2)",

        "& > $label": {
          background: "none !important",
          color: "#65AC1E",
        },
      },
    },
    content: {
      borderRadius: "4px",
      padding: "6px",
      height: "32px",
    },
    label: {
      color: "rgba(39, 58, 57, 0.8)",
      fontSize: "12px",
      lineHeight: "100%",
      fontWeight: "bold",
      background: "none !important",
    },
  })
);

const useItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginBottom: "2px",
      "& $content:hover": {
        background: "rgba(51, 51, 51, 0.05)",
      },
    },
    content: {
      borderRadius: "4px",
      padding: "6px",
      height: "32px",
    },
    label: {
      color: "rgba(39, 58, 57, 0.8)",
      fontSize: "12px",
      lineHeight: "100%",
      fontWeight: "bold",
      background: "none !important",
    },
    selected: {
      "& $content": {
        background: "rgba(101, 172, 30, 0.2)",
      },
      "& $label": {
        background: "none !important",
        color: "#65AC1E",
      },
    },
  })
);

export interface InputCalendarHierarchyItem {
  id: unknown;
  parent: unknown;
  name: string;
}

export interface CalendarHierarchyItem {
  id: string;
  parent?: string;
  origin: unknown;
  name: string;
}

interface CalendarHierarchyProps {
  label: string;
  value: unknown;
  onChange: (value: unknown) => void;
  items: InputCalendarHierarchyItem[] | undefined;
  multiple?: boolean;
  selectableParent?: boolean;
}

interface ContextMenu {
  nodeId: number;
  node: any;
  x: number;
  y: number;
}

export const CalendarHierarchy: FC<CalendarHierarchyProps> = (props) => {
  const intl = useIntl();
  const items: CalendarHierarchyItem[] = React.useMemo(
    () =>
      props.items?.map((item) => ({
        id: String(item.id),
        name: item.name,
        parent: item.parent === null || item.parent === undefined ? undefined : String(item.parent),
        origin: item.id,
      })) || [],
    [props.items]
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [contextMenu, setContextMenu] = React.useState<ContextMenu | null>(null);
  const classesItem = useItemStyles();
  const classesParent = useParentStyles();
  const propsValue = props.value;
  const [selected, setSelected] = React.useState<CalendarHierarchyItem[] | undefined | null>(undefined);
  const [opened, setOpened] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (items.length) {
      let nextSelected: CalendarHierarchyItem[] = [];
      if (props.multiple) {
        const multipleSelected = selected?.map((item) => item.origin);
        const sortedSelected = sortedUniq(multipleSelected);
        const sortedPropsValue = sortedUniq(propsValue as unknown[]);
        if (!equal(sortedPropsValue, sortedSelected)) {
          nextSelected = items.filter((item) => sortedPropsValue.includes(item.origin));
        }
      } else {
        const singleValue = (selected || [])[0];
        if (propsValue !== singleValue?.origin) {
          nextSelected = [items.find((item) => equal(item.origin, propsValue))!];
        }
      }
      if (nextSelected.length) {
        setSelected(nextSelected);
      }
    }
  }, [propsValue, items]);

  React.useEffect(() => {
    let nextOpened = (selected?.map((e) => e.parent).filter(Boolean) || []) as string[];
    const findSkipParents = () =>
      nextOpened
        .map((parentId) => {
          const parent = items.find((e) => e.id === parentId);
          if (parent?.parent && !nextOpened.includes(parent?.parent)) {
            return parent?.parent;
          }
          return undefined;
        })
        .filter(Boolean) as string[];
    let skipParents = findSkipParents();
    while (skipParents.length) {
      nextOpened.push(...skipParents);
      skipParents = findSkipParents();
    }
    setOpened(nextOpened);
  }, [selected]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      const selectedItem = document.querySelector('.tree-view [aria-selected="true"]');
      try {
        // For IE. block start
        selectedItem?.scrollIntoView();
        // For NO IE. block center
        selectedItem?.scrollIntoView({
          block: "end",
        });
      } catch (e) {}
    }, 100);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (props.multiple && props.onChange) {
      if (selected?.length) {
        props.onChange(selected?.map((x) => x.origin));
      } else {
        const sortedPropsValue = sortedUniq(propsValue as unknown[]);
        setSelected(items.filter((item) => sortedPropsValue.includes(item.origin)));
      }
    }
  };

  const treeViewClasses = useTreeStyles();
  const nodes = React.useMemo(() => hierarchies<any>(items, "id", "parent"), [items]);
  const openedNodes = React.useMemo(() => {
    const selectedNodes = selected?.flatMap((x) => hierarchyAllByChildId(nodes, x.id));
    return selectedNodes || [];
  }, [nodes, selected]);
  const open = Boolean(anchorEl);
  const togglePopover = (e) => (open ? handleClose() : handleClick(e));
  const handleSelect = React.useCallback(
    (e, id) => {
      if (!e.target.classList.contains("tree-parent-toggle") && !e.target.closest(".tree-parent-toggle")) {
        const hasChildren = items.some((item) => (props.multiple ? id.includes(item.parent) : item.parent === id));
        if (props.selectableParent || !hasChildren) {
          if (props.multiple) {
            let nextSelectedItems = selected || [];
            const excludedItems = nextSelectedItems.filter((item) => id.includes(item.id));
            const includedItems = items.filter((x) => id.includes(x.id) && !excludedItems.some((exitm) => exitm.id === x.id));
            nextSelectedItems = nextSelectedItems.filter((item) => !excludedItems.some((x) => x.id === item.id)).concat(includedItems);
            setSelected(sortCalendarItems(nextSelectedItems));
          } else {
            const selectedItem = items.find((x) => x.id === id)!;
            setSelected([selectedItem]);
            if (props.onChange) {
              props.onChange(selectedItem.origin);
            }
            handleClose();
          }
        }
      }
    },
    [selected, items]
  );

  const handlerToggle = React.useCallback((event: React.ChangeEvent<{}>, nodeIds: string[]) => {
    setOpened(nodeIds);
  }, []);

  const unselectAll = React.useCallback(() => {
    setSelected([]);
  }, []);

  const customNode = (hierarchy: Hierarchy<any>) => {
    if (hierarchy.children.length) {
      return (
        <TreeItem
          role="listitem"
          data-hierarhy={hierarchy.id}
          key={hierarchy.item.name}
          nodeId={hierarchy.id}
          label={hierarchy.item.name}
          className="tree-parentnode"
          classes={classesParent}
        >
          <div data-ignore-contextmenu={hierarchy.id}>{hierarchy.children.map(customNode)}</div>
        </TreeItem>
      );
    }
    return (
      <TreeItem
        role="listitem"
        key={hierarchy.item.name}
        nodeId={hierarchy.id}
        label={hierarchy.item.name}
        className="tree-node"
        classes={classesItem}
      />
    );
  };

  const contextMenuOpen = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (props.multiple) {
      const { clientX, clientY, target } = e;
      const isNode = Boolean((target as any).dataset["hierarhy"]);
      const parentNode = isNode ? (target as any) : (target as any).closest("[data-hierarhy]");
      const ignoreNode = (target as any).closest("[data-ignore-contextmenu]");
      const ignoreId = Number(ignoreNode?.dataset["ignore-contextmenu"]);
      const nodeId = Number(parentNode.dataset["hierarhy"]);
      const node = parentNode.children[0];
      if (nodeId && nodeId !== ignoreId) {
        setContextMenu({
          x: clientX,
          y: clientY,
          nodeId,
          node,
        });
      }
    }
  }, []);
  const contextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);
  const getAllContextMenuIds = () => {
    const hierarchies = hierarchiesFlat(nodes);
    const hierarchy = hierarchies.find((h) => +h.id === contextMenu?.nodeId)!;
    const childrenIds = hierarchiesGetAllIds([hierarchy], !props.selectableParent);
    return uniq((props.selectableParent ? [hierarchy?.id, ...childrenIds] : childrenIds).filter(Boolean));
  };
  const selectHierarchy = () => {
    const childrenIds = getAllContextMenuIds();
    let nextSelectedItems = selected || [];
    nextSelectedItems = nextSelectedItems.concat(items.filter((x) => childrenIds.includes(x.id)));
    setSelected(sortCalendarItems(nextSelectedItems));
    contextMenuClose();
  };

  const deselectHierarchy = () => {
    const childrenIds = getAllContextMenuIds();
    let nextSelectedItems = selected || [];
    nextSelectedItems = nextSelectedItems.filter((item) => !childrenIds.includes(item.id));
    setSelected(sortCalendarItems(nextSelectedItems));
    contextMenuClose();
  };

  if (!props.items) {
    return <Skeleton variant="rect" width="168px" height="51px" />;
  }

  let displayValue: string;
  if (props.multiple) {
    const displayValues = sortedUniq(selected?.map((x) => x.name) || []);
    displayValue = [displayValues[0], displayValues[displayValues.length - 1]].join(" - ");
  } else {
    displayValue = (selected && selected[0].name) || "";
  }
  const value = selected && (!props.multiple ? selected[0].id : selected.map((x) => x.id));
  return (
    <>
      <Input label={props.label} value={displayValue} open={open} onClick={togglePopover} />
      <Popover anchorEl={anchorEl} onClose={togglePopover}>
        <div className={cn(treeViewClasses.container, "calendar-hierarchy-content")}>
          <Menu open={!!contextMenu} anchorEl={contextMenu?.node} onClose={contextMenuClose} className={treeViewClasses.menu}>
            <MenuItem className={treeViewClasses.menuItem} onClick={selectHierarchy}>
              Select hierarchy
            </MenuItem>
            <MenuItem className={treeViewClasses.menuItem} onClick={deselectHierarchy}>
              Deselect hierarchy
            </MenuItem>
          </Menu>
          {value && (
            <div
              onContextMenu={contextMenuOpen}
              className={cn(props.multiple ? treeViewClasses.scrollBoxMultiple : treeViewClasses.scrollBox, "tree-view")}
            >
              <TreeView
                defaultCollapseIcon={<RemoveOutlinedIcon className="tree-parent-toggle" />}
                defaultExpandIcon={<AddBoxOutlinedIcon className="tree-parent-toggle" />}
                role="list"
                className={treeViewClasses.root}
                defaultExpanded={openedNodes.map((x) => x.id)}
                expanded={opened}
                selected={value as any}
                onNodeSelect={handleSelect}
                onNodeToggle={handlerToggle}
                multiSelect={props.multiple}
              >
                {!nodes?.length && "" ? "No results" : null}
                {nodes.map(customNode)}
              </TreeView>
            </div>
          )}
          {props.multiple ? (
            <UI.Button
              className={treeViewClasses.selectButton}
              title={intl.formatMessage(messages.unselectAllButtonLabel)}
              color="default"
              variant="text"
              size="small"
              onClick={unselectAll}
              disabled={!Boolean(value?.length)}
            >
              <UncheckedIcon className={treeViewClasses.selectButtonIcon} />
            </UI.Button>
          ) : null}
        </div>
      </Popover>
    </>
  );
};
